import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import FilterView from '../filter';
import Sort from '../sort';
import Content from '../body/content';
import Footer from '../body/footer';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  getMixEventAsyncAction, 
  setEvents, 
  setMixSelectedLeagues 
} from '../../common/actions/common.actions';
import { useSelector, useDispatch } from 'react-redux';
import { playSong, setConfirmData } from '../../common/actions/common.actions';
import { selectLoginData } from '../login/login.actions';
import { IoIosArrowBack } from "react-icons/io";
import { FiFilter, FiSearch } from "react-icons/fi";
import { LiaSortAmountDownSolid } from "react-icons/lia";
import { MdCancel } from "react-icons/md";
import { TbSoccerField } from "react-icons/tb";
import { BiFootball } from 'react-icons/bi';
import Modal from '../../common/components/modal';

const MaunggComponent = React.memo(({ isLoading, setIsLoading, showToastMessage }) => {
  const navigate = useNavigate();
  const { events, mixSelectedLeagues, mixEvents, confirmData } = useSelector((state) => state.common);
  const { userDetails, validMixCount } = useSelector(selectLoginData);
  const dispatch = useDispatch();
  
  const [showFilter, setShowFilter] = useState(false);
  const [leagues, setLeagues] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [showSorting, setShowSorting] = useState(false);
  const [sort, setSort] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [selectedMatches, setSelectedMatches] = useState([]);
  const [deselectAll, setDeselectAll] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalContent, setModalContent] = useState('');
  const [initialLoad, setInitialLoad] = useState(true);
  
  const { t } = useTranslation();
  
  // Use refs to track values without causing re-renders
  const selectedMatchesRef = useRef(selectedMatches);
  const confirmDataRef = useRef(confirmData);

  // Update refs when state changes
  useEffect(() => {
    selectedMatchesRef.current = selectedMatches;
  }, [selectedMatches]);

  useEffect(() => {
    confirmDataRef.current = confirmData;
  }, [confirmData]);

  // Effect 1: Initialize confirm data (runs only once)
  useEffect(() => {
    if (confirmData && confirmData.selectedMatches && Array.isArray(confirmData.selectedMatches)) {
      setSelectedMatches(confirmData.selectedMatches);
    }
  }, [confirmData]);

  // Effect 2: Process mixEvents when it changes - FIXED
  useEffect(() => {
    if (!mixEvents) return;

    const processEvents = () => {
      // Sort leagues - FIXED: Added null safety checks
      if (mixEvents.leagueList && Array.isArray(mixEvents.leagueList) && mixEvents.leagueList.length > 0) {
        const sortedLeagues = [...mixEvents.leagueList].sort((a, b) => {
          const nameA = a.leagueName || '';
          const nameB = b.leagueName || '';
          return nameA.localeCompare(nameB);
        });
        setLeagues(sortedLeagues);
      } else {
        setLeagues([]); // Set empty array if no leagues
      }

      // Process event data
      const eventData = [];
      if (mixEvents.eventDetails && Array.isArray(mixEvents.eventDetails) && mixEvents.eventDetails.length > 0) {
        mixEvents.eventDetails.forEach((item) => {
          if (item && item.eventList && Array.isArray(item.eventList) && item.eventList.length > 0) {
            item.eventList.forEach((eventItem) => {
              if (eventItem) {
                eventData.push({
                  ...eventItem,
                  leagueName: item.league || '',
                  leagueId: item.leagueId || '',
                });
              }
            });
          }
        });
      }

      // Sort matches by time with safety checks
      const sortedMatches = eventData.sort((a, b) => {
        const timeA = a.eventTime ? new Date(a.eventTime) : new Date();
        const timeB = b.eventTime ? new Date(b.eventTime) : new Date();
        return timeA - timeB;
      });
      
      // Dispatch events to Redux
      dispatch(setEvents(sortedMatches));

      // Initialize selected leagues if empty
      const leagueList = mixEvents.leagueList || [];
      if ((!mixSelectedLeagues || !Array.isArray(mixSelectedLeagues) || mixSelectedLeagues.length === 0) && 
          !deselectAll && 
          Array.isArray(leagueList) && 
          leagueList.length > 0) {
        const leagueNames = leagueList
          .filter(league => league && league.leagueName)
          .map((league) => league.leagueName);
      
        if (leagueNames.length > 0) {
          dispatch(setMixSelectedLeagues(leagueNames));
        }
      }

      setIsLoading(false);
    };

    processEvents();
  }, [mixEvents, dispatch, deselectAll, mixSelectedLeagues, setIsLoading]);

  // Effect 3: Filter invalid matches when events change
  useEffect(() => {
    if (!events || !Array.isArray(events) || events.length === 0) return;

    const validEventIDs = events.map((x) => x.eventId).filter(id => id != null);
    
    // Filter confirm data if it exists in Redux
    if (confirmData && 
        confirmData.selectedMatches && 
        Array.isArray(confirmData.selectedMatches) && 
        confirmData.selectedMatches.length > 0) {
      const validConfirmData = confirmData.selectedMatches.filter(x => 
        x && x.eventId && validEventIDs.includes(x.eventId)
      );
      
      // Only dispatch if data has actually changed
      if (validConfirmData.length !== confirmData.selectedMatches.length) {
        dispatch(setConfirmData({ 
          betAmount: confirmData.betAmount || 0, 
          selectedMatches: validConfirmData 
        }));
      }
    }
    
    // Filter selected matches using ref to avoid dependency loop
    const currentSelectedMatches = selectedMatchesRef.current;
    if (currentSelectedMatches && 
        Array.isArray(currentSelectedMatches) && 
        currentSelectedMatches.length > 0) {
      const validSelectedMatches = currentSelectedMatches.filter(x => 
        x && x.eventId && validEventIDs.includes(x.eventId)
      );
      
      // Only update if selection has actually changed
      if (validSelectedMatches.length !== currentSelectedMatches.length) {
        setSelectedMatches(validSelectedMatches);
      }
    }
  }, [events, confirmData, dispatch]);

  // Effect 4: Initial data fetch (runs only once)
  useEffect(() => {
    const fetchData = async () => {
      if (userDetails && initialLoad) {
        setIsLoading(true);
        try {
          await dispatch(getMixEventAsyncAction());
        } catch (error) {
          console.error("Failed to fetch mix events:", error);
          showToastMessage?.("Failed to load matches", "error");
        } finally {
          setInitialLoad(false);
        }
      }
    };

    fetchData();
  }, [userDetails, dispatch, setIsLoading, initialLoad, showToastMessage]);

  // Effect 5: Check for lock status
  useEffect(() => {
    if (mixEvents?.islock) {
      localStorage.removeItem('token');
      localStorage.removeItem('userDetails');
      navigate("/login");
    }
  }, [mixEvents, navigate]);

  const handleSongClick = useCallback(() => {
    dispatch(playSong());
  }, [dispatch]);

  const handleBackClick = useCallback(() => {
    handleSongClick();
    navigate('/dashboard');
  }, [handleSongClick, navigate]);

  const filteredMatches = useMemo(() => {
    if (!events || !Array.isArray(events) || events.length === 0) return [];

    // Filter by selected leagues first
    const leagueFiltered = mixSelectedLeagues && 
                          Array.isArray(mixSelectedLeagues) && 
                          mixSelectedLeagues.length > 0
      ? events.filter(event => 
          event && 
          event.leagueName && 
          mixSelectedLeagues.includes(event.leagueName)
        )
      : events;

    let sortedMatches = [...leagueFiltered];
    
    // Apply sorting
    if (sort === "leagueName") {
      sortedMatches.sort((a, b) => {
        const nameA = a.leagueName || '';
        const nameB = b.leagueName || '';
        return nameA.localeCompare(nameB);
      });
    } else if (sort === "eventTime") {
      sortedMatches.sort((a, b) => {
        const timeA = a.eventTime ? new Date(a.eventTime) : new Date();
        const timeB = b.eventTime ? new Date(b.eventTime) : new Date();
        return timeA - timeB;
      });
    }

    // Apply search filter
    if (searchInput.trim()) {
      const searchTerm = searchInput.toLowerCase();
      return sortedMatches.filter((event) => {
        if (!event) return false;
        
        return (
          (event.leagueName && event.leagueName.toLowerCase().includes(searchTerm)) ||
          (event.homeTeam && event.homeTeam.toLowerCase().includes(searchTerm)) ||
          (event.homeTeamMyan && event.homeTeamMyan.toLowerCase().includes(searchTerm)) ||
          (event.awayTeam && event.awayTeam.toLowerCase().includes(searchTerm)) ||
          (event.awayTeamMyan && event.awayTeamMyan.toLowerCase().includes(searchTerm))
        );
      });
    }

    return sortedMatches;
  }, [events, mixSelectedLeagues, searchInput, sort]);

  const handleFilterClick = useCallback(() => {
    handleSongClick();
    setShowFilter(prev => !prev);
  }, [handleSongClick]);

  const handleFilterApply = useCallback(() => {
    setShowFilter(false);
  }, []);

  const handleLeagueSelection = useCallback((selectedLeagues) => {
    if (!selectedLeagues || !Array.isArray(selectedLeagues)) {
      setDeselectAll(true);
      dispatch(setMixSelectedLeagues([]));
      return;
    }
    
    setDeselectAll(selectedLeagues.length === 0);
    dispatch(setMixSelectedLeagues(selectedLeagues));
  }, [dispatch]);

  const handleSortingSelection = useCallback((selectedSort) => {
    setSort(selectedSort);
  }, []);

  const handleSortingClick = useCallback(() => {
    handleSongClick();
    setShowSorting(prev => !prev);
  }, [handleSongClick]);

  const handleSortingApply = useCallback(() => {
    setShowSorting(false);
  }, []);

  const handleSearchClick = useCallback(() => {
    handleSongClick();
    setShowSearch(prev => !prev);
  }, [handleSongClick]);

  const handleSearchInputChange = useCallback((event) => {
    setSearchInput(event.target.value || '');
  }, []);

  const handleSelectTeam = useCallback((choosen, match, maxTeam = validMixCount, fromBody = false) => {
    if (!match || !match.eventId) return;
    
    setSelectedMatches(prevSelectedMatches => {
      const prevMatches = prevSelectedMatches && Array.isArray(prevSelectedMatches) ? prevSelectedMatches : [];
      
      const ind = prevMatches.length > 0
        ? prevMatches.findIndex((x) => x && x.eventId === match.eventId)
        : -1;
      
      const isSameItem = prevMatches.length > 0
        ? prevMatches.findIndex((x) => x && x.eventId === match.eventId && x.choosen === choosen) >= 0
        : false;

      if (!isSameItem && (prevMatches.length < maxTeam)) {
        if (prevMatches.length > 0) {
          return [
            ...(ind >= 0
              ? prevMatches.filter((x) => x && x.eventId !== match.eventId)
              : prevMatches),
            { choosen, ...match },
          ];
        } else {
          return [{ choosen, ...match }];
        }
      } else {
        if (fromBody && !isSameItem) {
          return [{ choosen, ...match }];
        } else {
          const newSelectedSameEvent = prevMatches.some((x) => 
            x && x.eventId === match.eventId && x.choosen !== choosen
          );
          if (newSelectedSameEvent) {
            return [
              ...(ind >= 0
                ? prevMatches.filter((x) => x && x.eventId !== match.eventId)
                : prevMatches),
              { choosen, ...match },
            ];
          }
          return prevMatches.filter((x) => x && x.eventId !== match.eventId);
        }
      }
    });
  }, [validMixCount]);

  const handleRemoveCard = useCallback((rapidEventId) => {
    if (!rapidEventId) return;

    setSelectedMatches(prev => {
      const prevMatches = prev && Array.isArray(prev) ? prev : [];
      const newData = prevMatches.filter(x => x && x.rapidEventId !== rapidEventId);

      // Update Redux if confirmData exists (read from ref to avoid dep)
      const currentConfirmData = confirmDataRef.current;
      if (currentConfirmData) {
        dispatch(setConfirmData({
          betAmount: currentConfirmData.betAmount || 0,
          selectedMatches: newData
        }));
      }

      return newData;
    });
  }, [dispatch]);

  const handleClearSearch = useCallback(() => {
    handleSongClick();
    setSearchInput('');
    setShowSearch(false);
  }, [handleSongClick]);

  const isWideWindow = useMemo(() => typeof window !== 'undefined' ? window.innerWidth >= 1024 : false, []);

  const renderMatches = () => {
    if (!filteredMatches || !Array.isArray(filteredMatches) || filteredMatches.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-10">
          <BiFootball size={48} className="mb-4 text-blue-primary/50" />
          <p className="text-lg font-medium text-blue-primary/70">
            {t('noMatchesFound') || 'No matches found'}
          </p>
        </div>
      );
    }

    return filteredMatches.map((match) => {
      if (!match || !match.eventId) return null;
      
      return (
        <li key={match.eventId} className="w-full">
          <Content
            match={match}
            sendDataToParent={handleSelectTeam}
            selectedMatches={selectedMatches}
            eventTime={match.eventTime}
            homeTeam={match.homeTeam}
            awayTeam={match.awayTeam}
            bodyHandicap={match.bodyHandicap}
            goalHandicap={match.goalHandicap}
          />
        </li>
      );
    }).filter(Boolean); // Remove null entries
  };

  return (
    <div className="w-full h-[100dvh] flex flex-col overflow-hidden relative bg-gradient-to-br from-silver via-gray-100 to-silver">
      <div className="relative z-10">
        {/* Professional Header Section */}
        <div className="w-full border-b-2 shadow-2xl bg-gradient-to-r from-white/95 via-white/98 to-white/95 backdrop-blur-xl border-crimson/40">
          <div className="px-2 py-3">
            <div className="flex items-center justify-between">
              {/* Left Section - Back Button & Title */}
              <div className="flex items-center">
                <button
                  className="flex items-center justify-center w-10 h-10 transition-all duration-300 text-blue-primary hover:bg-crimson/10 rounded-xl"
                  onClick={handleBackClick}
                >
                  <IoIosArrowBack size={24} />
                </button>
                {!showSearch && (
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center justify-center w-12 h-12 border-2 shadow-lg bg-gradient-to-br from-crimson via-red-primary to-crimson rounded-xl border-golden/40">
                      <TbSoccerField size={22} className="text-white drop-shadow-lg" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold leading-tight text-blue-primary">{t('maungg')}</h3>
                      <p className="text-xs font-medium text-blue-primary/70">Football Betting</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Search Input - Full Width When Active */}
              {showSearch && (
                <div className="flex-1 mx-4">
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full bg-white border-2 border-crimson/30 rounded-xl px-4 py-2.5 pl-10 pr-10 text-blue-primary placeholder-blue-primary/50 focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson transition-all duration-300"
                      placeholder="Search matches..."
                      value={searchInput}
                      onChange={handleSearchInputChange}
                    />
                    <FiSearch size={18} className="absolute transform -translate-y-1/2 left-3 top-1/2 text-blue-primary/70" />
                    <button
                      className="absolute transition-colors duration-300 transform -translate-y-1/2 right-3 top-1/2 text-crimson hover:text-blue-primary"
                      onClick={handleClearSearch}
                    >
                      <MdCancel size={20} />
                    </button>
                  </div>
                </div>
              )}

              {/* Right Section - Action Buttons */}
              {!showSearch && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleSortingClick}
                    className="flex items-center justify-center w-10 h-10 transition-all duration-300 text-blue-primary hover:bg-crimson/10 rounded-xl"
                    title="Sort"
                  >
                    <LiaSortAmountDownSolid size={20} />
                  </button>
                  <button
                    onClick={handleSearchClick}
                    className="flex items-center justify-center w-10 h-10 transition-all duration-300 text-blue-primary hover:bg-crimson/10 rounded-xl"
                    title="Search"
                  >
                    <FiSearch size={20} />
                  </button>
                  <button
                    onClick={handleFilterClick}
                    className="relative flex items-center justify-center w-10 h-10 transition-all duration-300 text-blue-primary hover:bg-crimson/10 rounded-xl"
                    title="Filter"
                  >
                    <FiFilter size={20} />
                    {mixSelectedLeagues && Array.isArray(mixSelectedLeagues) && mixSelectedLeagues.length > 0 && (
                      <div className="absolute w-3 h-3 border-2 border-white rounded-full -top-1 -right-1 bg-crimson"></div>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Luxury Border Accent */}
          <div className="absolute bottom-0 left-0 right-0 h-1 shadow-lg bg-gradient-to-r from-crimson via-golden to-crimson"></div>
        </div>
      </div>
      
      {/* Enhanced Main Content Area */}
      <div className="relative z-10 flex-1 overflow-auto bg-transparent">
        <div className="px-1 py-2">
          <ul className="flex flex-col w-full p-0 space-y-1 list-none scroll-smooth">
            {renderMatches()}
          </ul>
        </div>
      </div>

      {/* Enhanced Footer */}
      <div className="relative z-10 border-t-2 shadow-2xl bg-gradient-to-r from-white/95 via-white/98 to-white/95 backdrop-blur-xl border-crimson/40">
        {/* Luxury Border Accent */}
        <div className="absolute top-0 left-0 right-0 h-1 shadow-lg bg-gradient-to-r from-crimson via-golden to-crimson"></div>
        <Footer
          selectedMatches={selectedMatches}
          fromBody={false}
          showToastMessage={showToastMessage}
          handleRemoveCard={handleRemoveCard}
          onModalTitleChange={setModalTitle}
          onModalContentChange={setModalContent}
          onOpenModalChange={setOpenModal}
        />
      </div>

      {/* Enhanced Sort Dropdown */}
      {showSorting && (
        <div className="absolute top-20 right-4 bg-gradient-to-br from-white via-white/98 to-white/95 backdrop-blur-xl border-2 border-crimson/30 rounded-xl shadow-2xl p-4 z-40 min-w-[200px]">
          <Sort onClose={handleSortingApply} onSortingSelection={handleSortingSelection} />
        </div>
      )}

      {/* Enhanced Filter Overlay */}
      <div
        className={`fixed top-0 left-0 w-full ${isWideWindow ? 'w-[85%] left-60' : ''} h-[100dvh] bg-gradient-to-br from-silver via-gray-100 to-silver z-50 transform transition-transform duration-300 ${
          showFilter ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <FilterView
          onClose={handleFilterApply}
          leagues={leagues}
          onLeagueSelection={handleLeagueSelection}
          events={events && Array.isArray(events) ? events.length : 0}
          mixBody="maung"
        />
      </div>
      {openModal && (
        <Modal 
          title={modalTitle} 
          content={modalContent} 
          onClose={() => setOpenModal(false)} 
        />
      )}
    </div>
  );
});

export default MaunggComponent;